import { Button, Space, type TableProps, Typography } from 'antd';
import React from 'react';
import TableCellText from './TableCellText';

type WidthColumn = {
  width?: string | number;
  hideInTable?: boolean;
  children?: WidthColumn[];
};

type DisplayColumn = Omit<WidthColumn, 'children'> & {
  title?: unknown;
  children?: DisplayColumn[];
  dataIndex?: unknown;
  key?: React.Key;
  valueType?: unknown;
  copyable?: unknown;
  ellipsis?: boolean | { showTitle?: boolean };
  className?: string;
  render?: (...args: any[]) => any;
  onCell?: (...args: any[]) => React.TdHTMLAttributes<HTMLTableCellElement>;
};

const fieldWidths: Record<string, number> = {
  employeeNo: 180,
  mobile: 180,
  phone: 180,
  employeeName: 160,
  employeeUuid: 160,
  departmentName: 180,
  positionName: 180,
  remark: 240,
  description: 240,
  reason: 240,
  adjustmentReason: 240,
  createdAt: 180,
  updatedAt: 180,
  operatedAt: 180,
  publishedAt: 180,
  submittedAt: 180,
  completedAt: 180,
};

function defaultWidth(column: DisplayColumn) {
  if (typeof column.key === 'string' && fieldWidths[column.key])
    return fieldWidths[column.key];
  if (typeof column.dataIndex === 'string' && fieldWidths[column.dataIndex])
    return fieldWidths[column.dataIndex];
  if (column.valueType === 'dateTime') return 180;
  if (column.valueType === 'date') return 120;
  if (column.valueType === 'option') return 180;
  return 160;
}

export function tableScroll<T extends object>(scroll: TableProps<T>['scroll']) {
  return {
    ...scroll,
    x:
      scroll?.x === undefined || typeof scroll.x === 'number'
        ? 'max-content'
        : scroll.x,
  };
}

function fitCellText(value: any, isDataColumn: boolean): any {
  if (typeof value === 'string' || typeof value === 'number')
    return React.createElement(TableCellText, null, value);
  if (!React.isValidElement<Record<string, any>>(value)) return value;
  const { children, style } = value.props;
  if (value.type === Space || value.type === React.Fragment)
    return React.cloneElement(
      value,
      value.type === Space
        ? { style: { minWidth: 0, maxWidth: '100%', ...style } }
        : {},
      React.Children.map(children, (child) => fitCellText(child, isDataColumn)),
    );
  if (typeof children !== 'string' && typeof children !== 'number')
    return value;
  if (value.type === Typography.Text && !value.props.editable)
    return React.cloneElement(value, {
      ellipsis: value.props.ellipsis ?? { tooltip: children },
      style: { minWidth: 0, maxWidth: '100%', ...style },
    });
  if (
    (value.type === Button && value.props.type === 'link' && isDataColumn) ||
    value.type === 'a' ||
    (value.type === 'span' && !style?.whiteSpace)
  )
    return React.cloneElement(
      value,
      {},
      React.createElement(TableCellText, null, children),
    );
  return value;
}

export function withTableColumnDisplay<T extends DisplayColumn>(
  columns: T[] | undefined,
  kind: 'pro' | 'simple',
): Array<T & DisplayColumn> | undefined {
  if (!columns) return columns;
  const normalize = (items: DisplayColumn[]): DisplayColumn[] =>
    items.map((column) => {
      if (column.children)
        return {
          ...column,
          children: normalize(column.children),
        };
      if (column.hideInTable) return column;
      const sized = { ...column, width: column.width ?? defaultWidth(column) };
      // Explicit multiline and action columns keep their own rendering rules.
      if (column.ellipsis === false || column.valueType === 'option')
        return sized;
      const proText = kind === 'pro' && !column.render;
      return {
        ...sized,
        ellipsis: proText
          ? (column.ellipsis ?? true)
          : kind === 'simple' && !column.render
            ? (column.ellipsis ?? { showTitle: false })
            : column.ellipsis,
        className: [column.className, 'app-table-cell-single-line']
          .filter(Boolean)
          .join(' '),
        onCell: (...args) => ({
          title: '',
          ...column.onCell?.(...args),
        }),
        ...(proText && column.copyable
          ? {
              render: (node: React.ReactNode) =>
                React.isValidElement<Record<string, any>>(node) &&
                node.type === Typography.Text &&
                node.props.copyable?.text === '-'
                  ? React.cloneElement(node, { copyable: false })
                  : node,
            }
          : column.render || kind === 'simple'
            ? {
                render: (...args: any[]) => {
                  const value = column.render
                    ? column.render(...args)
                    : args[0];
                  return fitCellText(value, column.dataIndex !== undefined);
                },
              }
            : {}),
      };
    });
  return normalize(columns) as Array<T & DisplayColumn>;
}

export function withTableColumnWidths<T extends WidthColumn>(
  columns: T[] | undefined,
  fixed: boolean,
): T[] | undefined {
  if (!fixed || !columns) return columns;
  const normalize = (items: WidthColumn[]): WidthColumn[] =>
    items.map((column) =>
      column.children
        ? { ...column, children: normalize(column.children) }
        : column.width === undefined && !column.hideInTable
          ? { ...column, width: 120 }
          : column,
    );
  return normalize(columns) as T[];
}
